import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit
} from '@angular/core';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
import {
    AbstractControl,
    ControlValueAccessor,
    FormBuilder,
    FormControl,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    Validator,
    Validators
} from '@angular/forms';
import { AccountSelectModel } from './account.select.model';
import { Observable, Subject } from 'rxjs';
import { AcctsQuery } from 'src/app/shared/state/accts/accts.query';
import { AccountTypeEnum, Acct } from 'src/app/shared/state/accts/acct.model';
import { AcctsService } from 'src/app/shared/state/accts/accts.service';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { CommonModule } from '@angular/common';
import { SbAccountSelectComponent } from '../form/sb-account-select/sb-account-select.component';

@Component({
    selector: 'sb-accounts-selector',
    templateUrl: './accounts-selector.component.html',
    styleUrls: ['./accounts-selector.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: AccountsSelectorComponent
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: AccountsSelectorComponent
        }
    ],
    standalone: true,
    imports: [
        CommonModule,
        MatFormFieldModule,
        MatSelectModule,
        ReactiveFormsModule,
        SbAccountSelectComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountsSelectorComponent
    implements OnInit, OnDestroy, ControlValueAccessor, Validator
{
    endOfToday: Date = new Date();

    get touched(): boolean {
        return this._touched;
    }

    @Input() set touched(value: boolean) {
        this._touched = value;
        if (this.touched) {
            this.accountForm.markAllAsTouched();
        } else {
            this.accountForm.markAsUntouched();
        }
    }
    get useBrokerAccount(): boolean {
        return this._useBrokerAccount;
    }

    @Input() set useBrokerAccount(value: boolean) {
        this._useBrokerAccount = value;
        if (this.useBrokerAccount) {
            this.accountForm.controls.brokerAccount.addValidators([Validators.required]);
        } else {
            this.accountForm.controls.brokerAccount.removeValidators([
                Validators.required
            ]);
            this.accountForm.controls.brokerAccount.clearValidators();
        }
    }

    @Input() set usePaymentAccount(value: boolean) {
        this._usePaymentAccount = value;
        if (this.usePaymentAccount) {
            this.accountForm.controls.paymentAccount.addValidators([Validators.required]);
        } else {
            this.accountForm.controls.paymentAccount.removeValidators([
                Validators.required
            ]);
            this.accountForm.controls.paymentAccount.clearValidators();
        }
    }

    @Input()
    account: Acct;
    @Input()
    paymentAccount: Acct;
    @Input()
    brokerAccount: Acct;
    private _useBrokerAccount: boolean = false;
    private brokerAccountLength: number = 0;
    @Input()
    includeBrokerAccountsAsSelectableMainAccount: boolean = false;
    private _usePaymentAccount: boolean = true;
    @Input()
    displayPaymentAccount: boolean = true;
    @Input()
    private mainAccountIsEligibleForCash: boolean = true;
    @Input()
    private mainAccountIsEligibleForSecuritiesAndDerivatives: boolean = true;
    @Input()
    private mainAccountIsEligibleForLoans: boolean = true;
    @Input()
    private mainAccountIsEligibleForPhysicalAssets: boolean = true;
    @Input()
    displayOnSingle: boolean = false;
    allSelectorsVisible: boolean = false;
    unsubscribe$ = new Subject();

    accountForm = this._fb.group<{
        [K in keyof AccountSelectModel]: FormControl<AccountSelectModel[K]>;
    }>({
        account: new FormControl<Acct>(null, [Validators.required]),
        paymentAccount: new FormControl<Acct>(null, [Validators.required]),
        brokerAccount: new FormControl<Acct>(null)
    });
    private _touched: boolean = false;

    accounts$: Observable<{
        accounts: Acct[];
        brokerAccounts: Acct[];
        paymentAccounts: Acct[];
    }> = this._accountsQuery.accounts$.pipe(
        filter((accs) => accs?.length > 0), // Ignore emits without values (init account state)
        map((accs) => {
            return {
                accounts: accs.filter(
                    (a) =>
                        this.isEligibleSecuritiesAccount(a) ||
                        (this.includeBrokerAccountsAsSelectableMainAccount &&
                            this.isBrokerAccount(a))
                ),
                paymentAccounts: accs.filter((a) => this.isEligiblePaymentAccount(a)),
                brokerAccounts: accs.filter(
                    (a) => this._useBrokerAccount && this.isBrokerAccount(a)
                )
            };
        }),
        tap((accts) => {
            // Don't overwrite if values have been set when initializing the control (in formgroup using account selector).
            if (
                Object.values(this.accountForm.controls).some((fc) => fc.value !== null)
            ) {
                return;
            }
            // Set initial values (notifies formgroup using account selector)
            setTimeout(() =>
                this.accountForm.patchValue({
                    account: accts.accounts?.length === 1 ? accts.accounts[0] : null,
                    paymentAccount:
                        accts.paymentAccounts?.length === 1
                            ? accts.paymentAccounts[0]
                            : null,
                    brokerAccount:
                        this._useBrokerAccount && accts.brokerAccounts?.length === 1
                            ? accts.brokerAccounts[0]
                            : null
                })
            );

            // All selectors will be visible if none of the account selectors have just one account (see markup).
            // In addition, broker account is toggleable through _useBrokerAccount property.
            // --
            // No broker account = not submitted, not displayed
            // One broker account = it is automatically selected, not displayed
            // Two or more = always displayed
            this.allSelectorsVisible =
                accts.accounts?.length !== 1 &&
                accts.paymentAccounts?.length !== 1 &&
                accts.brokerAccounts?.length > 1 &&
                this._useBrokerAccount;

            // Store length of broker accounts in component so we can use it
            // in the validation check
            if (accts.brokerAccounts?.length > 0 && this._useBrokerAccount) {
                this.brokerAccountLength = accts.brokerAccounts.length;
            }
        })
    );

    onTouched: Function = () => {};
    onChange: Function = (accounts) => {};

    constructor(private _accountsQuery: AcctsQuery, private _fb: FormBuilder) {}

    ngOnInit() {
        this.endOfToday.setHours(23, 59, 59, 999);
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }

    isBrokerAccount(account: Acct): boolean {
        return (
            account.accountType === AccountTypeEnum.BROKER &&
            (account.closingDate ? account.closingDate >= this.endOfToday : true)
        );
    }

    /**
     * According to what accounts we populate the dropdown with today.
     */
    isEligibleSecuritiesAccount(account: Acct): boolean {
        return (
            account.accountType !== AccountTypeEnum.BROKER &&
            account.accountType !== AccountTypeEnum.PORTFOLIO &&
            (account.closingDate ? account.closingDate >= this.endOfToday : true) &&
            ((this.mainAccountIsEligibleForSecuritiesAndDerivatives &&
                account.eligibleForSecuritiesAndDerivatives === true) ||
                (this.mainAccountIsEligibleForCash && account.eligibleForCash === true) ||
                (this.mainAccountIsEligibleForLoans &&
                    account.eligibleForLoans === true) ||
                (this.mainAccountIsEligibleForPhysicalAssets &&
                    account.eligibleForPhysicalAssets === true))
        );
    }

    /**
     * Returns the same accounts as the transaction account, with the addition
     * of broker accounts as a workaround for some clients to "get rid of" cash
     * transactions without having to see them. To be removed when a better solution
     * is in place.
     */
    isEligiblePaymentAccount(account: Acct): boolean {
        return (
            account.accountType !== AccountTypeEnum.PORTFOLIO &&
            (account.closingDate ? account.closingDate >= this.endOfToday : true) &&
            account.eligibleForCash === true
        );
    }

    writeValue(accounts: AccountSelectModel): void {
        if (accounts) {
            this.accountForm.controls.account.setValue(accounts.account);
            this.accountForm.controls.paymentAccount.setValue(accounts.paymentAccount);
            this.accountForm.controls.brokerAccount.setValue(accounts.brokerAccount);
        }
    }

    registerOnChange(onChange: any): void {
        this.accountForm.valueChanges
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(onChange);
        this.onChange = onChange;
    }

    registerOnTouched(onTouched: any): void {
        this.onTouched = onTouched;
    }

    setDisabledState?(disabled: boolean): void {
        if (disabled) {
            this.accountForm.disable();
        } else {
            this.accountForm.enable();
        }
    }

    validate(control: AbstractControl) {
        if (this.accountForm.valid) {
            return null;
        }

        let errors: any = {};

        errors = this.addControlErrors(errors, 'account');
        errors = this.addControlErrors(errors, 'paymentAccount');

        // Only if there is at least one broker account we add validation for
        // the broker account.
        if (this.brokerAccountLength > 0) {
            errors = this.addControlErrors(errors, 'brokerAccount');
        }

        return errors;
    }

    addControlErrors(allErrors: any, controlName: string) {
        const errors = { ...allErrors };
        const controlErrors = this.accountForm.controls[controlName].errors;

        if (controlErrors) {
            errors[controlName] = controlErrors;
        }

        return errors;
    }
}
